import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { EmptyState, Loading, Refusal } from "@lopie/kernel-ui";

import Answer from "../components/Answer.jsx";
import FilterRow from "../components/FilterRow.jsx";
import { addItem, applyShared, describeShared, itemSpec, removeItem, sharedFields, MAX_ITEMS } from "../lib/dashboards.js";
import { nothingToShow } from "../lib/declared.js";
import { isBadQuestion } from "../lib/run.js";
import { defaultOp } from "../lib/spec.js";

const update = (list, index, patch) => list.map((x, i) => (i === index ? { ...x, ...patch } : x));
const without = (list, index) => list.filter((_, i) => i !== index);

/**
 * One chart on a dashboard: its question, narrowed by what the dashboard shares,
 * asked of the kernel as the person looking. It keeps its own answer, so one
 * chart that cannot be answered does not blank the others.
 */
function Tile({ item, schema, shared, runKey, query, onAnswer, onDrill, onRemove, canEdit, dropEmpty, onSettled }) {
  const [out, setOut] = useState(null);
  const { state, notes } = useMemo(() => applyShared(item, schema, shared), [item, schema, shared]);

  useEffect(() => {
    let live = true;
    const spec = itemSpec(item, schema, shared);
    onSettled?.(item.id, null);
    if (!spec) { setOut({ ok: false, unfinished: true }); onSettled?.(item.id, true); return undefined; }
    setOut(null);
    query(spec).then((answer) => {
      if (!live) return;
      setOut({ ...answer, state });
      if (answer.ok) onAnswer?.(answer.result);
      // A failure is shown, not dropped: only an answer with nothing in it is left off.
      onSettled?.(item.id, !(answer.ok && nothingToShow(answer.result)));
    });
    return () => { live = false; };
    // `runKey` is what says "ask again": filters held in the form are not sent
    // until they are applied, so a keystroke is not a request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey, item.id, query]);

  // A dashboard a connector suggests leaves off a chart with nothing in it, and draws none until it knows.
  if (dropEmpty && (!out || (out.ok && nothingToShow(out.result)))) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }} component="section" aria-label={item.title}>
      <Stack sx={{ gap: 1 }}>
        <Stack direction="row" sx={{ gap: 1, justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
              {item.title}
            </Typography>
          </Box>
          {canEdit ? (
            <Button size="small" color="inherit" onClick={onRemove} aria-label={`Remove ${item.title} from this dashboard`}>
              Remove
            </Button>
          ) : null}
        </Stack>

        {notes.map((n) => (
          <Alert key={n} severity="info" variant="outlined" sx={{ py: 0 }}>{n}</Alert>
        ))}

        {!out ? (
          <Loading label="Asking the kernel" minHeight={120} />
        ) : out.unfinished ? (
          <Alert severity="warning">This chart has nothing to count yet. Open it in the builder to finish it.</Alert>
        ) : out.ok ? (
          <Answer result={out.result} state={out.state} schema={schema} onDrill={onDrill} />
        ) : isBadQuestion(out.error) ? (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>The kernel did not accept that question.</Typography>
            <Typography variant="body2">{out.error.message}</Typography>
          </Alert>
        ) : (
          <Refusal failure={out.error} actor="Insights" />
        )}
      </Stack>
    </Paper>
  );
}

/**
 * One dashboard, with its own shared filters. Keyed by the dashboard's id by the
 * caller, so switching dashboards starts from that dashboard's own filters and
 * never asks a question with the previous one's.
 */
function DashboardView({ dashboard, saved, schema, query, onAnswer, onDrill, onChange }) {
  // Read-only: a connector's suggestion, or somebody else's that was shared with this person.
  // Either can be narrowed for the person looking, and nothing they do to it is kept.
  const readOnly = Boolean(dashboard.readOnly);
  // Only a connector's leaves off charts with nothing in them: a person's own dashboard shows
  // every chart it holds, empty or not, so nothing they made seems to have gone missing.
  const dropEmpty = Boolean(dashboard.source);
  const [pick, setPick] = useState("");
  // Which charts have answered and have something in them. null is still asking.
  const [settled, setSettled] = useState({});
  const onSettled = useCallback((id, value) => setSettled((s) => (s[id] === value ? s : { ...s, [id]: value })), []);
  const pending = dropEmpty && dashboard.items.some((i) => settled[i.id] === undefined || settled[i.id] === null);
  const anyShown = dashboard.items.some((i) => settled[i.id] === true);
  // What the form holds is not what is applied: a request per keystroke against
  // a service that counts records would be a cost nobody asked for.
  const [draft, setDraft] = useState({ range: dashboard.range, filters: dashboard.filters });
  const [applied, setApplied] = useState({ range: dashboard.range, filters: dashboard.filters });
  const [runKey, setRunKey] = useState(0);

  const fields = useMemo(() => sharedFields(dashboard, schema), [dashboard, schema]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);
  const empty = { range: { from: "", to: "" }, filters: [] };
  const nothingShared = (x) => !x.range.from && !x.range.to && !x.filters.length;

  const apply = useCallback(() => {
    setApplied(draft);
    if (!readOnly) onChange(dashboard.id, (cur) => ({ ...cur, range: draft.range, filters: draft.filters }));
    setRunKey((n) => n + 1);
  }, [draft, dashboard, onChange, readOnly]);

  const clear = () => {
    setDraft(empty);
    setApplied(empty);
    if (!readOnly) onChange(dashboard.id, (cur) => ({ ...cur, ...empty }));
    setRunKey((n) => n + 1);
  };

  const addable = saved.filter((r) => !dashboard.items.some((i) => i.sourceId === r.id));
  const full = dashboard.items.length >= MAX_ITEMS;

  return (
    <>
      {dropEmpty ? (
        <Alert severity="info" variant="outlined">
          Suggested by the {dashboard.source} connector{dashboard.description ? `: ${dashboard.description}` : "."}{" "}
          It holds no figures: each chart is asked of the kernel for you when it is drawn.
        </Alert>
      ) : readOnly ? (
        <Alert severity="info" variant="outlined">
          Shared with you by {dashboard.owner}. You can narrow it for yourself and nothing you change is kept;
          every chart is answered for the records <em>you</em> can open.
        </Alert>
      ) : null}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack sx={{ gap: 1.5 }}>
          <Typography variant="subtitle2" component="h3">Narrow every chart</Typography>
          <Typography variant="caption" color="text.secondary">
            A date range narrows each chart over the date it is drawn by. A filter narrows each chart that has the
            field, and a chart that does not says so.
          </Typography>
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small" type="date" label="From" value={draft.range.from}
              onChange={(e) => setDraft({ ...draft, range: { ...draft.range, from: e.target.value } })}
              slotProps={{ inputLabel: { shrink: true } }} sx={{ minWidth: 170 }}
            />
            <TextField
              size="small" type="date" label="To" value={draft.range.to}
              onChange={(e) => setDraft({ ...draft, range: { ...draft.range, to: e.target.value } })}
              slotProps={{ inputLabel: { shrink: true } }} sx={{ minWidth: 170 }}
            />
          </Stack>
          {draft.filters.map((f, i) => (
            <FilterRow
              key={i} f={f} fields={fields} label="Where"
              onChange={(patch) => setDraft({ ...draft, filters: update(draft.filters, i, patch) })}
              onRemove={() => setDraft({ ...draft, filters: without(draft.filters, i) })}
            />
          ))}
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            {fields.length ? (
              <Button size="small"
                onClick={() => setDraft({ ...draft, filters: [...draft.filters, { field: fields[0].name, op: defaultOp(fields[0]), value: "" }] })}>
                Add a filter
              </Button>
            ) : null}
            <Button variant="contained" size="small" onClick={apply} disabled={!dirty}>Apply</Button>
            <Button size="small" color="inherit" onClick={clear} disabled={nothingShared(draft) && nothingShared(applied)}>
              Clear
            </Button>
            <Typography variant="caption" color="text.secondary" role="status">
              Narrowed by: {describeShared(applied)}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {readOnly ? null : (
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          select size="small" label="Add a saved chart" value={pick}
          onChange={(e) => setPick(e.target.value)} sx={{ minWidth: 280 }}
          disabled={!addable.length || full}
          helperText={!saved.length ? "Save a chart from the builder first." : undefined}
        >
          {addable.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
        </TextField>
        <Button
          disabled={!pick || full}
          onClick={() => {
            const r = saved.find((x) => x.id === pick);
            if (r) onChange(dashboard.id, (cur) => addItem(cur, r.name, r.state, { sourceId: r.id }));
            setPick("");
          }}
        >
          Add
        </Button>
        {full ? <Typography variant="caption" color="text.secondary">A dashboard holds {MAX_ITEMS} charts.</Typography> : null}
      </Stack>
      )}

      {pending ? <Loading label="Asking the kernel" minHeight={120} /> : null}
      {dropEmpty && !pending && !anyShown ? (
        <EmptyState title="Nothing to show">
          <Typography variant="body2">No records you can open match any chart on this dashboard.</Typography>
        </EmptyState>
      ) : null}

      {dashboard.items.length === 0 && !dropEmpty ? (
        <EmptyState title="No charts on this dashboard yet">
          <Typography variant="body2">
            {readOnly ? "The person who shared it has not added any." : "Add one of your saved charts above."}
          </Typography>
        </EmptyState>
      ) : (
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" } }}>
          {dashboard.items.map((item) => (
            <Tile
              key={item.id} item={item} schema={schema} shared={applied} runKey={runKey}
              query={query} onAnswer={onAnswer} onDrill={onDrill} canEdit={!readOnly} dropEmpty={dropEmpty} onSettled={onSettled}
              onRemove={() => onChange(dashboard.id, (cur) => removeItem(cur, item.id))}
            />
          ))}
        </Box>
      )}
    </>
  );
}

/**
 * Several charts on one page, narrowed together.
 *
 * A dashboard is saved in the kernel and can be shared with people and groups,
 * like a report. It holds questions, not answers: two people who open the same
 * one are each answered for what they may open, so each sees their own slice.
 * Ones a person made, ones shared with them and ones a connector suggests are
 * three lists, and only the first can be edited, shared or removed here.
 */
export default function Dashboards({
  dashboards, declared = [], declaredFailed = false, loading = false, failed = false,
  local, moving, onMove, selectedId, onSelect, onCreate, onDelete, onChange, onShare, onHistory,
  saved, schema, query, onAnswer, onDrill,
}) {
  const dashboard = [...dashboards, ...declared].find((d) => d.id === selectedId) || null;
  const [name, setName] = useState("");
  const mine = dashboards.filter((d) => !d.readOnly);
  const shared = dashboards.filter((d) => d.readOnly);

  return (
    <Stack sx={{ gap: 2 }}>
      {failed ? (
        <Alert severity="warning" role="alert">Your dashboards could not be read from the kernel just now, so this list may be incomplete.</Alert>
      ) : null}
      {declaredFailed ? (
        <Alert severity="warning" variant="outlined">
          The dashboards your connectors suggest could not be read. Yours are not affected.
        </Alert>
      ) : null}
      {local?.count ? (
        <Alert
          severity="info" variant="outlined"
          action={<Button color="inherit" size="small" disabled={moving} onClick={onMove}>{moving ? "Moving…" : "Move them"}</Button>}
        >
          {local.count} saved in this browser from before reports were kept in the kernel. Move them to keep them
          with your account and share them.
        </Alert>
      ) : null}
      <Alert severity="info" variant="outlined">
        A dashboard holds questions and not answers: everyone who opens one is answered for the records they can
        open, so two people see their own figures.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          {dashboards.length || declared.length ? (
            <TextField
              select size="small" label="Dashboard" value={dashboard?.id || ""}
              onChange={(e) => onSelect(e.target.value)} sx={{ minWidth: 280 }}
            >
              {mine.length ? <ListSubheader>Yours</ListSubheader> : null}
              {mine.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              {shared.length ? <ListSubheader>Shared with you</ListSubheader> : null}
              {shared.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              {declared.length ? <ListSubheader>From your connectors</ListSubheader> : null}
              {declared.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          ) : null}
          <TextField
            size="small" label="New dashboard" value={name}
            onChange={(e) => setName(e.target.value)} sx={{ minWidth: 220 }}
          />
          <Button disabled={!name.trim()} onClick={() => { onCreate(name.trim()); setName(""); }}>Create</Button>
          {dashboard && !dashboard.readOnly ? (
            <>
              <Button onClick={() => onShare(dashboard)} aria-label={`Share the dashboard ${dashboard.name}`}>Share</Button>
              <Button onClick={() => onHistory(dashboard)} aria-label={`Versions of the dashboard ${dashboard.name}`}>Versions</Button>
              <Button color="inherit" onClick={() => onDelete(dashboard)} aria-label={`Delete the dashboard ${dashboard.name}`}>
                Delete this dashboard
              </Button>
            </>
          ) : null}
        </Stack>
      </Paper>

      {loading ? (
        <Loading label="Reading your dashboards" minHeight={120} />
      ) : !dashboard ? (
        <EmptyState title={dashboards.length || declared.length ? "Pick a dashboard" : "No dashboards yet"}>
          <Typography variant="body2">
            Name one above, then add charts to it from the ones you have saved.
          </Typography>
        </EmptyState>
      ) : (
        <DashboardView
          key={dashboard.id} dashboard={dashboard} saved={saved} schema={schema} query={query}
          onAnswer={onAnswer} onDrill={onDrill} onChange={onChange}
        />
      )}
    </Stack>
  );
}
